import { EuiToolTip } from "@equipmentshare/ds2";
import * as DsIcon from "@equipmentshare/ds-icon";
import { ReadonlyArray, pipe } from "@morphism/fp";
import { notification } from "antd";
import React from "react";
import { useQueryClient } from "react-query";
import styled from "styled-components";
import moment from "moment";

import { Branch } from "~/Branch";
import * as Button from "~/Button";
import * as Data from "~/Data";
import * as Driver from "~/Driver";

import * as Job from "~/Job";
import * as Layout from "~/Layout";
import * as Log from "~/Log";
import { Remote } from "~/Remote";
import * as Table from "~/Table";
import { Time } from "~/Time";
import * as Typography from "~/Typography";
import { WebAPI } from "~/WebAPI";
import * as WorkCode from "~/WorkCode";
import { WorkOrder } from "~/WorkOrder";
import { RevisionStatusWarning } from "./RevisionStatusWarning";

import * as Request from "./Request";

export * as Form from "./Form";
export * as Request from "./Request";

/** Revisions allow admins to modify `Event`s if accepted by the driver */
export type Revision = Data.Reference.Reference &
  Log.Event.Event & {
    readonly driver: Data.Reference.Reference;
    readonly workOrder?: Data.Reference.Reference;
    readonly workCodes?: readonly Data.Reference.Reference[];
    readonly job?: Data.Reference.Reference;
    readonly sources: Log.Event.Source.Sources.Sources & {
      readonly current: Log.Event.Source.Revision;
    };
  };

export const isForLog = (log: Log.Identity) => (revision: Revision): boolean =>
  revision.driver.id === log.driver.id &&
  Time.Interval.Stopped.collides(revision)(Time.DateTime.toDayInterval(log));

export const useFromRoute = (route: Log.Route): Remote<readonly Revision[]> =>
  pipe(
    Remote.Query.use<readonly Revision[]>(
      Request.Get.Query.key({ driver: route.driver }),
      Request.Get.Query.request(WebAPI.useClient())(route.driver),
      {
        keepPreviousData: true,
        staleTime: Time.Duration.minutes(5).duration,
      }
    ),
    Remote.map((suggestions) =>
      suggestions.filter((suggestion) =>
        Time.Interval.Stopped.collides(Time.DateTime.toDayInterval(route))(
          pipe(suggestion, ({ interval }) =>
            Time.Interval.when(
              { interval },
              {
                started: (started) =>
                  Time.Interval.Stopped.fromDateTimes(
                    started.interval.start,
                    Time.DateTime.now()
                  ),
                stopped: (stopped) => stopped,
              }
            )
          )
        )
      )
    )
  );

export const Component = (route: Log.Route) => {
  const suggestions = Log.Revision.useFromRoute(route);
  const revisionDrawerControl = Log.Revision.Form.Drawer.use(route);

  const onAddRevision = (suggestion: Log.Suggestion.Suggestion) => {
    revisionDrawerControl.show(suggestion);
  };

  const [homeOffice, zone] = Driver.useFromReference({
    ...route.driver,
    found: (driver) => [driver.homeOffice, driver.time],
    missing: () => [undefined, undefined],
  });

  const branch = Branch.FromLocation.useOrUndefined(homeOffice?.location);
  const allowRevisions = Time.DateTime.isBeforeCurrentDay(route, zone);

  const timeZone = zone?.zone;
  const startDateInDriverTz = timeZone ? moment(route.dateTime).tz(timeZone, true) : undefined;
  const startDateIsoString = startDateInDriverTz ? startDateInDriverTz.toISOString() : undefined;

  return (
    <Layout.Section.Component>
      <Layout.Block.Component all>
        <Typography.H2.Component>Suggested Revisions</Typography.H2.Component>
        {startDateIsoString && <RevisionStatusWarning
          startDate={startDateIsoString}
          driverId={route.driver.id}
        />}
      </Layout.Block.Component>
      <Table.Scroll.Component>
        <Log.Table.Component>
          {pipe(
            Remote.when(suggestions, {
              success: () => <Revisions {...route} />,
              loading: () => (
                <>
                  <Table.Wide.Loading.Component colSpan={Log.Table.colSpan()} />
                  <Revisions {...route} />
                </>
              ),
              error: (error) => (
                <Table.Wide.ErrorMessage.Component
                  colSpan={Log.Table.colSpan()}
                >
                  {error.message}
                </Table.Wide.ErrorMessage.Component>
              ),
            })
          )}
          <Table.Footer.Component>
            <Log.Revision.Form.Drawer {...branch} {...revisionDrawerControl} />
            <CreateRevision.Component
              suggestion={Log.Suggestion.create(route)}
              onAddRevision={onAddRevision}
              disabled={!allowRevisions}
              tooltip={
                allowRevisions
                  ? undefined
                  : "Same day revisions are not allowed."
              }
            />
          </Table.Footer.Component>
        </Log.Table.Component>
      </Table.Scroll.Component>
    </Layout.Section.Component>
  );
};

const Revisions: Component<Log.Route> = (route: Log.Route) => {
  const queryClient = useQueryClient();
  const deleteSuggestion = Log.Revision.Request.Patch.Delete.use();

  const onDelete = (suggestion: Data.Reference.Reference) => {
    deleteSuggestion.request(suggestion);
  };

  React.useEffect(
    () =>
      Remote.when(deleteSuggestion.response, {
        idle: () => {},
        loading: () => {},
        error: () => {
          deleteSuggestion.reset();
          void notification.error({
            message: "Error",
            description: "Unable to delete a revision suggestion.",
            duration: 6,
            icon: (
              <DsIcon.Icon
                name="$icon_check_circle_fill"
                color="$color_primary_green"
              />
            ),
            placement: "topRight",
          });
        },
        success: () => {
          deleteSuggestion.reset();
          void notification.success({
            message: "Success",
            description: "Revision suggestion successfully deleted",
            duration: 5,
            icon: (
              <DsIcon.Icon
                name="$icon_check_circle_fill"
                color="$color_primary_green"
              />
            ),
            placement: "topRight",
          });
          void queryClient.invalidateQueries(
            Log.Revision.Request.Get.Query.key(route)
          );
        },
      }),
    [deleteSuggestion.response]
  );

  const suggestions = Remote.toUndefined(Log.Revision.useFromRoute(route));

  return suggestions && suggestions[0] ? (
    <Table.Body.Component>
      {pipe(
        suggestions,
        ReadonlyArray.map((suggestion) => (
          <Row key={suggestion.id} revision={suggestion} onDelete={onDelete} />
        ))
      )}
    </Table.Body.Component>
  ) : (
    <Table.Wide.Empty.Component colSpan={Log.Table.colSpan()} />
  );
};

const Row = ({
  revision,
  onDelete,
}: {
  readonly revision: Data.Reference.Reference & Partial<Revision>;
  readonly onDelete: (suggestion: Data.Reference.Reference) => void;
}) => {
  return (
    <Table.Row.Component>
      <Log.Table.Status.Component {...revision} />
      <Log.Table.Time.Component {...revision} />
      <Log.Table.Location.Component {...revision} />
      <Log.Table.Source.Component {...revision} />
      <Table.Column.Component>
        {revision.workOrder && (
          <WorkOrder.Link target="_blank" workOrder={revision.workOrder} />
        )}
      </Table.Column.Component>
      <Table.Column.Component>
        {revision.workCodes && revision.workCodes.length && revision.workCodes.map(workCode => (
          <>
            <WorkCode.Link.Component
              target="_blank"
              workCode={workCode}
            />
            <br />
          </>
        ))}
      </Table.Column.Component>
      <Table.Column.Component>
        {revision.job && (
          <Job.Link.Component target="_blank" job={revision.job} />
        )}
      </Table.Column.Component>
      <Log.Table.Notes.Component {...revision} />
      <Log.Table.Delete.Component revision={revision} onDelete={onDelete} />
    </Table.Row.Component>
  );
};

namespace CreateRevision {
  export const Component = ({
    suggestion,
    onAddRevision,
    disabled,
    tooltip,
  }: {
    readonly suggestion: Log.Suggestion.Suggestion;
    readonly onAddRevision: (suggestion: Log.Suggestion.Suggestion) => void;
    readonly disabled?: boolean;
    readonly tooltip?: string;
  }) => (
    <Table.Row.Component hover={false}>
      <EmptyColumns.Component colSpan={Log.Table.colSpan() - 1} />
      <Table.Column.Component>
        <EuiToolTip position="top" content={tooltip}>
          <span>
            <Button.Component
              action={!disabled}
              neutral={disabled}
              disabled={disabled}
              blank
              title="Revisions"
              icon="$icon_add_fill"
              onClick={() => onAddRevision(suggestion)}
            />
          </span>
        </EuiToolTip>
      </Table.Column.Component>
    </Table.Row.Component>
  );

  namespace EmptyColumns {
    export const Component = styled(Table.Column.Component)`
      width: 100%;
    `;
  }
}
